import { PassSide } from "./zoneNameHelpers";

/**
 * Sizing a side-pass zone in whole traffic lanes.
 *
 * A director thinks in lanes — "warn me about anything passing within two lanes" — but nothing
 * persists a lane count: `LeftPassLanes`/`RightPassLanes` exist only as transient in-cab overrides
 * the firmware holds in memory and drops on reboot. The durable setting is the zone itself, so a
 * lane count is stored as, and read back from, the zone's width.
 *
 * Deliberately measured by size rather than by lane lines: lane lines are a detection feature that
 * may or may not be configured on a given truck, and a side-pass zone has to be well-defined either
 * way.
 *
 * Geometry matches the layout editor's convention (see the dashboard's attenuatorLayoutHelpers):
 * metres, x lateral and centred on the device, y negative going away from the truck into oncoming
 * traffic.
 */

/** Standard US lane. Matches the 12 ft the in-cab side-pass control already documents. */
export const LANE_WIDTH_FEET = 12;
export const METRES_PER_FOOT = 0.3048;
export const LANE_WIDTH_M = LANE_WIDTH_FEET * METRES_PER_FOOT;

/** Matches `MaxSidePassLanes` in DriverControlsController — the cab and the console must agree. */
export const MIN_SIDE_PASS_LANES = 1;
export const MAX_SIDE_PASS_LANES = 4;

/**
 * What a protected zone may measure, in metres.
 *
 * Here rather than in the dashboard's layout helpers because both consoles write this footprint and
 * the dashboard draws it — a bound only one of them knows is a bound the others can walk past, which
 * is how a zone the editor refuses to show gets created in the first place.
 */
export const MIN_ZONE_DIM_M = 0.5;
export const MAX_ZONE_WIDTH_M = 6;
export const MAX_ZONE_LENGTH_M = 15;

export const zoneWidthOutOfRange = (metres: number): boolean => metres < MIN_ZONE_DIM_M || metres > MAX_ZONE_WIDTH_M;
export const zoneLengthOutOfRange = (metres: number): boolean => metres < MIN_ZONE_DIM_M || metres > MAX_ZONE_LENGTH_M;

export type ZonePoint = { x: number; y: number };

export function clampLanes(lanes: number): number {
    if (!Number.isFinite(lanes)) return MIN_SIDE_PASS_LANES;
    return Math.min(MAX_SIDE_PASS_LANES, Math.max(MIN_SIDE_PASS_LANES, Math.round(lanes)));
}

export function lanesToWidthMetres(lanes: number): number {
    return clampLanes(lanes) * LANE_WIDTH_M;
}

/** Lanes a width represents, rounded to the nearest whole lane and clamped to what the cab accepts. */
export function widthMetresToLanes(widthMetres: number): number {
    return clampLanes(widthMetres / LANE_WIDTH_M);
}

/** Width of a zone's footprint, in metres — the x extent, which is what lanes are stored as. */
export function zoneWidthMetres(points: readonly ZonePoint[] | null | undefined): number | undefined {
    if (!points || points.length === 0) return undefined;
    const xs = points.map((p) => p.x ?? 0);
    const width = Math.max(...xs) - Math.min(...xs);
    return width > 0 ? width : undefined;
}

/** Lane count a side-pass zone currently represents, or undefined if its shape can't be read. */
export function lanesFromZonePoints(points: readonly ZonePoint[] | null | undefined): number | undefined {
    const width = zoneWidthMetres(points);
    return width === undefined ? undefined : widthMetresToLanes(width);
}

/**
 * A side-pass zone: the protected zone's own footprint, moved to one side and widened to `lanes`.
 *
 * The length is taken from the protected zone rather than chosen here, so the two always watch the
 * same stretch of road and a truck with an unusual cushion gets side zones that match it. Only the
 * lateral extent comes from the lane count — widening from one lane to two never silently changes
 * how far up the road the alert looks.
 *
 * Returns undefined when the protected zone has no usable footprint, because a side zone guessed
 * from nothing would be a fabricated claim about where a driver gets warned.
 */
export function buildSidePassZonePoints(options: {
    side: PassSide;
    lanes: number;
    /** The protected zone this sits beside — supplies the length and the edge to start from. */
    protectedZonePoints: readonly ZonePoint[] | null | undefined;
}): ZonePoint[] | undefined {
    const { side, lanes, protectedZonePoints } = options;
    if (!protectedZonePoints || protectedZonePoints.length < 3) return undefined;

    const xs = protectedZonePoints.map((p) => p.x ?? 0);
    const ys = protectedZonePoints.map((p) => p.y ?? 0);

    // Start at the protected zone's edge on that side, so the two abut rather than overlap.
    const inner = side === "left" ? Math.min(...xs) : Math.max(...xs);
    const outer = side === "left" ? inner - lanesToWidthMetres(lanes) : inner + lanesToWidthMetres(lanes);

    const nearY = Math.max(...ys);
    const farY = Math.min(...ys);

    return [
        { x: inner, y: nearY },
        { x: outer, y: nearY },
        { x: outer, y: farY },
        { x: inner, y: farY },
    ];
}
